import React from "react";
import { Linkedin, ArrowUpRight } from "lucide-react";

const LEADERS = [
  {
    name: "LaMarqus Collins",
    role: "CTO / Cloud Architect",
    expertise: "SaaS Engineering & AWS GovCloud",
    bio:
      "Architects secure, cost-efficient cloud landing zones for public sector workloads. Fifteen-plus years across FedRAMP-authorized environments and mission-critical SaaS delivery.",
    initials: "LC",
    accent: "from-cyan-500/40 to-sky-500/10",
    focus: ["AWS GovCloud (US)", "Landing zones", "Zero-trust"],
  },
  {
    name: "Ollie Wilkins",
    role: "Compliance Director",
    expertise: "Cybersecurity & NIST Auditing",
    bio:
      "Owns control mapping and ATO package curation. Deep NIST SP 800-53 and 800-171 background across state and federal audit engagements. 3PAO liaison for every wave.",
    initials: "OW",
    accent: "from-amber-500/40 to-orange-500/10",
    focus: ["NIST 800-53 Rev.5", "CMMC L2", "POA&M closure"],
  },
  {
    name: "Tanika Kellam",
    role: "Senior Project Manager",
    expertise: "Agile Implementation & Governance",
    bio:
      "Runs delivery cadence — wave planning, risk registers, and cutover runbooks. Certified PMP and SAFe Agilist with a decade running mission-critical public sector programs.",
    initials: "TK",
    accent: "from-emerald-500/40 to-teal-500/10",
    focus: ["PMP", "SAFe Agilist", "Program governance"],
  },
];

export default function Leadership({ SectionHeader }) {
  return (
    <section id="leadership" className="relative py-24 md:py-32 px-6 md:px-10 border-t border-slate-800/50">
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader
          index="08"
          kicker="Leadership"
          title="Named principals. On every engagement."
          description="When you contract Eco Sweeps, you get direct access to the three people accountable for delivery, security posture, and program cadence. No junior offshoring, no faceless account managers."
        />

        <div className="grid md:grid-cols-3 gap-6 reveal">
          {LEADERS.map((L, i) => (
            <article
              key={L.name}
              className="panel corner-brackets p-8 hover-lift flex flex-col"
            >
              <div className="flex items-start justify-between mb-6">
                <div
                  className={`relative h-16 w-16 rounded-full bg-gradient-to-br ${L.accent} flex items-center justify-center border border-slate-700`}
                >
                  <span className="font-mono text-lg font-semibold text-slate-50 tracking-wider">{L.initials}</span>
                  <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-emerald-400 border-2 border-slate-950" title="Available" />
                </div>
                <span className="font-mono text-[10px] tracking-widest text-slate-500">{`0${i + 1}`}</span>
              </div>

              <h3 className="text-xl font-semibold text-slate-50 tracking-tight leading-snug">{L.name}</h3>
              <div className="eyebrow mt-1 mb-4">{L.role}</div>
              <div className="eyebrow-muted mb-4">{L.expertise}</div>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">{L.bio}</p>

              <div className="mt-auto">
                <div className="flex flex-wrap gap-2 mb-5">
                  {L.focus.map((f) => (
                    <span key={f} className="font-mono text-[10px] tracking-widest uppercase text-slate-400 border border-slate-800 px-2 py-1">
                      {f}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between border-t border-slate-800 pt-4">
                  <span className="font-mono text-[10px] tracking-widest text-emerald-400 uppercase inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> On engagement
                  </span>
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300 link-underline"
                  >
                    Route inquiry <ArrowUpRight size={11} />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-slate-800 pt-5">
          <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">
            Extended bench available on request · cleared surge capacity via prime partners
          </div>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-cyan-300 link-underline"
          >
            Schedule a leadership intro <Linkedin size={12} />
          </a>
        </div>
      </div>
    </section>
  );
}
